import React, { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useTranslation } from 'next-i18next'
import { graphqlAPI } from '../utils/graphql'
import { ticketsAPI } from '../utils/api'
import { 
  validateEmail, 
  validateVerificationCode,
  isEmailVerifiedInSession,
  storeEmailVerification,
  clearEmailVerification
} from '../utils/helpers'
import config from '../config/config'
import './EmailVerification.scss'

const EmailVerification = () => {
  const navigate = useNavigate()
  const { t, i18n } = useTranslation()

  const [orderData, setOrderData] = useState(null)
  const [step, setStep] = useState('email') // 'email' or 'verification'
  const [email, setEmail] = useState('')
  const [verificationCode, setVerificationCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resendCooldown, setResendCooldown] = useState(0)
  const [statusInfo, setStatusInfo] = useState(null)
  const [showStatus, setShowStatus] = useState(false)

  useEffect(() => {
    // Check if we have order data from previous step
    const storedOrder = sessionStorage.getItem('ticketOrder')
    if (!storedOrder) {
      navigate('/')
      return
    }

    const order = JSON.parse(storedOrder)
    setOrderData(order)

    // Check if email is already verified in session
    if (order.email && isEmailVerifiedInSession(order.email, order.eventId)) {
      // Email is already verified in this session, proceed to cart
      const updatedOrder = { ...order, emailVerified: true }
      sessionStorage.setItem('ticketOrder', JSON.stringify(updatedOrder))
      navigate('/cart')
      return
    }

    // If order already has an email, pre-fill it
    if (order.email) {
      setEmail(order.email)
    }
  }, [navigate])

  useEffect(() => {
    // Countdown for resend button
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1)
      }, 1000)
      return () => clearTimeout(timer)
    }
  }, [resendCooldown])

  const handleEmailSubmit = async (e) => {
    e.preventDefault()

    if (!validateEmail(email)) {
      setError(t('errors.invalidEmail'))
      return
    }

    try {
      setLoading(true)
      setError('')

      await graphqlAPI.sendVerificationCode(email, orderData.eventId, orderData.quantity, i18n.language)

      // Store email for next step
      const updatedOrder = { ...orderData, email }
      sessionStorage.setItem('ticketOrder', JSON.stringify(updatedOrder))
      setOrderData(updatedOrder)

      setStep('verification')
      setResendCooldown(config.EMAIL_VERIFICATION.RESEND_DELAY_SECONDS)
    } catch (err) {
      const serverMessage = err?.response?.errors?.[0]?.message || err?.message || ''
      const msg = (serverMessage || '').toLowerCase()
      if (msg.includes('invalid or expired verification code')) {
        setError(t('errors.invalidOrExpiredCode'))
      } else if (msg.includes('invalid verification code')) {
        setError(t('errors.invalidVerificationCode'))
      } else if (msg.includes('failed to store verification code')) {
        setError(t('errors.storeVerificationFailed'))
      } else {
        setError(serverMessage || t('errors.unknown'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleVerificationSubmit = async (e) => {
    e.preventDefault()

    if (!validateVerificationCode(verificationCode)) {
      setError(t('errors.invalidVerificationCode'))
      return
    }

    try {
      setLoading(true)
      setError('')

      const result = await graphqlAPI.verifyEmailCode(email, verificationCode)

      if (result && result.success) {
        // Store verification status in session and order data
        storeEmailVerification(email, orderData.eventId)
        
        // Store the auth token if present
        if (result.token) {
          sessionStorage.setItem('ticketUserToken', result.token)
        }
        
        const updatedOrder = { ...orderData, emailVerified: true }
        sessionStorage.setItem('ticketOrder', JSON.stringify(updatedOrder))

        // Auto-advance to cart on successful verification
        navigate('/cart')
        return

        // Fetch existing tickets/payment status for this email (legacy info flow)
        try {
          const resp = await ticketsAPI.getUserTicketsByEmail(email)
          const tickets = resp?.data?.tickets || []
          // Filter by current event
          const eventTickets = tickets.filter(t => String(t.event_id) === String(orderData.eventId))
          const count = eventTickets.length
          // Derive payment status and amounts from the latest payment for this event (if any)
          let paymentStatus = null
          let amountPaid = 0
          let totalAmount = 0
          let amountPending = 0
          if (eventTickets.length > 0) {
            // Pick most recent by generated_at
            const latest = [...eventTickets].sort((a, b) => new Date(b.generated_at) - new Date(a.generated_at))[0]
            paymentStatus = latest.payment_status
            amountPaid = Number(latest.amount_paid || 0)
            totalAmount = Number(latest.total_amount || 0)
            amountPending = Number(latest.amount_pending || Math.max(totalAmount - amountPaid, 0))
          }

          setStatusInfo({
            count,
            paymentStatus,
            amountPaid,
            totalAmount,
            amountPending
          })
          setShowStatus(true)
        } catch (fetchErr) {
          // If status fetch fails, still allow proceeding
          console.warn('Failed to fetch user tickets by email:', fetchErr)
          setShowStatus(false)
        }
      } else {
        setError(t('errors.invalidVerificationCode'))
      }
    } catch (err) {
      const serverMessage = err?.response?.errors?.[0]?.message || err?.message || ''
      const msg = (serverMessage || '').toLowerCase()
      if (msg.includes('failed to store verification code')) {
        setError(t('errors.storeVerificationFailed'))
      } else {
        setError(serverMessage || t('errors.unknown'))
      }
    } finally {
      setLoading(false)
    }
  }

  const handleResendCode = async () => {
    if (resendCooldown > 0) return

    try {
      setLoading(true)
      setError('')

      await graphqlAPI.sendVerificationCode(email, orderData.eventId, orderData.quantity, i18n.language)
      setResendCooldown(config.EMAIL_VERIFICATION.RESEND_DELAY_SECONDS)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleBackToEmail = () => {
    setStep('email')
    setVerificationCode('')
    setError('')
  }

  if (!orderData) {
    return (
      <div className="email-verification">
        <div className="container">
          <div className="text-center">
            <div className="spinner"></div>
            <p className="mt-3">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="email-verification">
      <div className="container">
        <div className="row justify-content-center">
          <div className="col-lg-6 col-md-8">
            {/* Progress Indicator (placed above the card for consistency) */}
            <div className="progress-indicator mb-4">
              <div className="step completed">
                <div className="step-number">1</div>
                <span>{t('verification.stepTickets')}</span>
              </div>
              <div className={`step ${step === 'email' || step === 'verification' ? 'active' : ''}`}>
                <div className="step-number">2</div>
                <span>{t('verification.stepEmail')}</span>
              </div>
              <div className="step">
                <div className="step-number">3</div>
                <span>{t('verification.stepPayment')}</span>
              </div>
            </div>

            <div className="verification-card card">
              <div className="card-body">
                {/* Purchase Summary */}
                <div className="purchase-summary mb-4">
                  <h4 className="purchase-summary-title">
                    ★ {t('verification.purchaseSummary')} ★
                  </h4>
                  <table className="purchase-summary-table">
                    <thead>
                      <tr>
                        <th>{t('verification.event')}</th>
                        <th>{t('verification.qty')}</th>
                        <th>{t('verification.price')}</th>
                        <th>{t('verification.total')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{orderData.eventName}</td>
                        <td>{orderData.quantity}</td>
                        <td>{orderData.pricePerTicket}€</td>
                        <td>{orderData.total}€</td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {step === 'email' ? (
                  /* Email Input Step */
                  <div className="email-step">
                    <h3>{t('verification.emailTitle')}</h3>
                    <p className="detail-item mb-4">
                      {t('verification.emailSubtitle')}
                    </p>

                    <form onSubmit={handleEmailSubmit}>
                      <div className="form-group mb-3">
                        <label htmlFor="email" className="form-label">
                          {t('verification.emailLabel')}
                        </label>
                        <input
                          type="email"
                          id="email"
                          className="form-control"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder={t('verification.emailPlaceholder')}
                          required
                          disabled={loading}
                        />
                      </div>

                      {error && (
                        <div className="alert alert-danger mb-3">
                          {error}
                        </div>
                      )}

                      <div className="form-actions">
                        <Link to={`/event/${orderData.eventId}/tickets`} className="back-link left me-3">
                          {t('common.back')}
                        </Link>
                        <button
                          type="submit"
                          className="btn btn-primary"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="spinner-sm me-2"></span>
                              {t('verification.sending')}
                            </>
                          ) : (
                            t('verification.sendCode')
                          )}
                        </button>
                      </div>
                    </form>
                  </div>
                ) : (
                  /* Verification Code Step */
                  <div className="verification-step">
                    <h3>{t('verification.codeTitle')}</h3>
                    <p className="detail-item mb-4">
                      {t('verification.codeSubtitle', { email })}
                    </p>

                    {showStatus && statusInfo && (
                      <div className="alert alert-info mb-3">
                        {statusInfo.count > 0 ? (
                          <>
                            <div>
                              {t('verification.status.registered', { count: statusInfo.count })}
                            </div>
                            {statusInfo.paymentStatus === 'PAID' && (
                              <div>{t('verification.status.purchased')}</div>
                            )}
                            {statusInfo.paymentStatus === 'PENDING' && (
                              <div>{t('verification.status.pending')}</div>
                            )}
                            {statusInfo.paymentStatus === 'PARTIALLY_PAID' && (
                              <div>
                                {t('verification.status.partial', { paid: statusInfo.amountPaid, total: statusInfo.totalAmount })}
                              </div>
                            )}
                          </>
                        ) : (
                          <div>{t('verification.status.notRegistered')}</div>
                        )}
                      </div>
                    )}

                    <form onSubmit={handleVerificationSubmit}>
                      <div className="form-group mb-3">
                        <label htmlFor="code" className="form-label">
                          {t('verification.codeLabel')}
                        </label>
                        <input
                          type="text"
                          id="code"
                          className="form-control code-input"
                          value={verificationCode}
                          onChange={(e) => setVerificationCode(e.target.value.toUpperCase())}
                          placeholder={t('verification.codePlaceholder')}
                          maxLength="6"
                          required
                          disabled={loading}
                        />
                        <small className="form-text detail-item">
                          {t('verification.codeHelp')}
                        </small>
                      </div>

                      {error && (
                        <div className="alert alert-danger mb-3">
                          {error}
                        </div>
                      )}

                      <div className="resend-section mb-3">
                        <p className="detail-item mb-2">
                          {t('verification.noCode')}
                          <button 
                          type="button"
                          className="btn btn-link p-3"
                          onClick={handleResendCode}
                          disabled={loading || resendCooldown > 0}
                        >
                          {resendCooldown > 0 ? (
                            t('verification.resendIn', { seconds: resendCooldown })
                          ) : (
                            t('verification.resendCode')
                          )}
                        </button>
                        </p>
                        
                      </div>

                      <div className="form-actions">
                        <button
                          type="button"
                          className="btn btn-outline me-3"
                          onClick={handleBackToEmail}
                          disabled={loading}
                        >
                          {t('verification.changeEmail')}
                        </button>
                        {showStatus ? (
                          <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => navigate('/cart')}
                            disabled={loading}
                          >
                            {t('verification.continueToCart')}
                          </button>
                        ) : (
                          <button
                            type="submit"
                            className="btn btn-primary"
                            disabled={loading}
                          >
                            {loading ? (
                              <>
                                <span className="spinner-sm me-2"></span>
                                {t('verification.verifying')}
                              </>
                            ) : (
                              t('verification.verify')
                            )}
                          </button>
                        )}
                      </div>
                    </form>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default EmailVerification
